import { Dispatch, SetStateAction } from 'react';
import { ace_index, lender } from '@axo/shared/data-access/types';
import { useLenders } from '@axo/shared/data-access/hooks';
import { MultiFilterSelect } from '../MultiFilterSelect';
import { setPageNumber } from '../SearchResults';

interface Props {
  query: ace_index.FindInput;
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>;
  marketCountry: string;
  editable?: boolean;
}

export function ApprovedBy({
  query,
  setQuery,
  marketCountry,
  editable = false,
}: Props) {
  const lendersRaw = useLenders(marketCountry);
  const options = lendersRaw.data ?? [];

  const state = getApprovedBy(query);
  const values = options.filter((option) => state?.includes(option.ID));

  return (
    <MultiFilterSelect
      items={options}
      onSelect={(option) => setQuery(addApprovedBy(option))}
      onRemove={(_, i) => setQuery(removeApprovedBy(values[i]))}
      stringify={(option) => option.ShortName}
      values={values}
      label="Approved by"
      description="Filter applications that have approved quotes from lenders"
      disabled={!editable}
    />
  );
}

function addApprovedBy(
  lender: lender.Lender
): SetStateAction<ace_index.FindInput> {
  return (query) => {
    const lenders = new Set(query.ApprovedLoanQuoteLenderIDs);

    if (lenders.has(lender.ID)) {
      lenders.delete(lender.ID);
    } else {
      lenders.add(lender.ID);
    }

    const next = Array.from(lenders.values());

    return {
      ...setPageNumber(1)(query),
      ApprovedLoanQuoteLenderIDs: next.length > 0 ? next : undefined,
    };
  };
}

function removeApprovedBy(
  lender: lender.Lender
): SetStateAction<ace_index.FindInput> {
  return (query) => {
    if (!query.ApprovedLoanQuoteLenderIDs) {
      return query;
    }

    const next = query.ApprovedLoanQuoteLenderIDs.filter(
      (id) => id !== lender.ID
    );

    return {
      ...setPageNumber(1)(query),
      ApprovedLoanQuoteLenderIDs: next.length > 0 ? next : undefined,
    };
  };
}

function getApprovedBy(query: ace_index.FindInput) {
  return query.ApprovedLoanQuoteLenderIDs;
}
